/**
 * /learn (docs/HINTS-LAYOUT-TUTORIAL.md Part B): one scripted hole against Ada
 * on the real table, driven by TutorialGame through the table's guide hook
 * (game/guide.ts). Never saved; the solo save is never touched.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Action, GameState } from '../../engine';
import type { TableGuide } from '../game/guide';
import type { SeatInfo, TableHandlers, TableModel } from '../game/types';
import { navigate, speedFromUrl } from '../lib/router';
import { KEYS, load, save } from '../lib/storage';
import { TableScreen } from '../screens/TableScreen';
import { markTutorialDone } from './keys';
import { Progress } from './Progress';
import { filterTargets } from './targets';
import { WRONG_TAP_LINE } from './script';
import { TutorialCoach } from './TutorialCoach';
import { TutorialFinish } from './TutorialFinish';
import { TutorialGame } from './tutorialGame';
import './tutorial.css';

function seatsFor(state: GameState): SeatInfo[] {
  return state.players.map((p) => ({
    name: p.name,
    isBot: p.isBot,
    ...(p.botLevel ? { level: p.botLevel } : {}),
    connected: true,
    autopilot: false,
    ready: true,
  }));
}

const noop = () => {};

export function TutorialScreen() {
  const [game] = useState(() => new TutorialGame({ name: load(KEYS.name, '') || 'You', speed: speedFromUrl() }));
  const [snap, setSnap] = useState(() => ({ view: game.view, idx: game.stepIndex }));
  const [run, setRun] = useState(0);
  const [finished, setFinished] = useState(false);
  /** Wrong taps in the current step, and a counter that bumps the nudge animation. */
  const [wrong, setWrong] = useState({ idx: -1, count: 0, nudge: 0 });

  useEffect(() => {
    game.onChange = () => setSnap({ view: game.view, idx: game.stepIndex });
    game.start();
    return () => {
      game.onChange = undefined;
      game.stop();
    };
  }, [game]);

  const step = game.steps[snap.idx]!;
  const exit = useCallback(() => {
    game.stop();
    navigate({ name: 'home' });
  }, [game]);
  const finish = useCallback(() => {
    markTutorialDone();
    save(KEYS.coach, true);
    setFinished(true);
  }, []);
  const replay = useCallback(() => {
    game.restart();
    setWrong({ idx: -1, count: 0, nudge: 0 });
    setRun((r) => r + 1);
    setFinished(false);
  }, [game]);
  const onReject = useCallback(() => {
    const idx = game.stepIndex;
    const counts = game.step.kind === 'do';
    setWrong((w) => ({ idx, count: (w.idx === idx ? w.count : 0) + (counts ? 1 : 0), nudge: w.nudge + 1 }));
  }, [game]);
  const next = useCallback(() => game.next(), [game]);
  const sheet = useCallback(() => game.sheetOpened(), [game]);

  const wrongHere = wrong.idx === snap.idx ? wrong.count : 0;
  const line = wrongHere >= 3 ? WRONG_TAP_LINE : step.line;

  const guide = useMemo<TableGuide>(
    () => ({
      filterTargets: (t) => filterTargets(t, game.allowedNow()),
      onReject,
      onFinished: finish,
      onExit: exit,
      topCenter: <Progress lesson={step.lesson} />,
      overlay: finished ? null : (
        <TutorialCoach
          stepIndex={snap.idx}
          step={step}
          line={line}
          nudge={wrong.idx === snap.idx ? wrong.nudge : 0}
          pulse={step.kind === 'do'}
          onNext={next}
          onExit={exit}
          onSheet={sheet}
        />
      ),
    }),
    // snap is in the deps so filterTargets re-runs on every state change
    [game, snap, step, line, wrong, finished, onReject, finish, exit, next, sheet],
  );

  const handlers = useMemo<TableHandlers>(
    () => ({
      act: (a: Action) => {
        game.act(a);
      },
      ready: noop,
      emote: noop,
      playAgain: noop,
      home: () => navigate({ name: 'home' }),
      pause: noop,
    }),
    [game],
  );

  const model: TableModel = { mode: 'solo', state: snap.view, me: 0, isHost: true, seats: seatsFor(snap.view), gameId: 'tutorial' };
  return (
    <>
      <TableScreen key={run} model={model} handlers={handlers} guide={guide} />
      {finished && <TutorialFinish onReplay={replay} />}
    </>
  );
}
